export const notificationChannels = ["inApp", "email", "realtime", "discord", "signal", "whatsapp"] as const;
export type NotificationChannel = (typeof notificationChannels)[number];

export const notificationEventKeys = [
  "app.offline",
  "app.online",
  "app.updateAvailable",
  "app.updated",
  "qb.updateAvailable",
  "dashboard.updateAvailable",
  "signal.updateAvailable",
  "news.announcement",
  "system.degraded",
  "system.diskUsage",
  "security.newLogin",
  "security.sessionRevoked",
  "security.passwordChanged",
  "security.mfaChanged",
  "job.completed",
  "job.failed",
  "support.ticketUpdated",
  "support.chatMessage",
  "digest.daily",
  "digest.weekly",
  "quietHours.enabled",
  // Admin-scoped events — visible only to users with admin privileges.
  // The prefs UI must gate these behind an "is admin?" check so non-admins
  // never see these toggles. Dispatchers fan these out to admin users only.
  "admin.app.offline",
  "admin.app.online",
  "admin.vpn.killSwitch",
  "admin.vpn.failback",
  "admin.vpn.recovery",
  "admin.monitor.alert",
  "admin.phpFpm.offline",
  "admin.phpFpm.online",
  "admin.security.attackSpike",
  "admin.transfer.activity"
] as const;
export type NotificationEventKey = (typeof notificationEventKeys)[number];

/**
 * Event keys that are only meaningful for admin users. The prefs UI should
 * render these toggles only when the viewer is an admin. Dispatchers for
 * these events resolve admin recipients on the backend.
 *
 * Mirrored from `src/domain/notifications/preferences.ts` in the backend —
 * keep in sync.
 */
export const adminOnlyNotificationEventKeys = [
  "signal.updateAvailable",
  "news.announcement",
  "admin.app.offline",
  "admin.app.online",
  "admin.vpn.killSwitch",
  "admin.vpn.failback",
  "admin.vpn.recovery",
  "admin.monitor.alert",
  "admin.phpFpm.offline",
  "admin.phpFpm.online",
  "admin.security.attackSpike",
  "admin.transfer.activity"
] as const;
export type AdminOnlyNotificationEventKey = (typeof adminOnlyNotificationEventKeys)[number];

/**
 * Returns true if the event key is part of the admin-scoped taxonomy.
 * Used by the prefs UI to gate admin-only toggle rows and entire groups.
 */
export const isAdminOnlyEventKey = (key: string): key is AdminOnlyNotificationEventKey =>
  (adminOnlyNotificationEventKeys as readonly string[]).includes(key);

export type NotificationEventSettings = {
  enabled: boolean;
  channels?: Partial<Record<NotificationChannel, boolean>> | undefined;
  comingSoon?: boolean | undefined;
  /**
   * Route this event's Discord message to a specific named webhook (by id).
   * Null/absent ⇒ the user's default webhook (see {@link resolveDiscordWebhookUrl}).
   * A referenced-but-missing id falls back to the default — never dropped silently.
   */
  discordWebhookId?: string | null | undefined;
  /**
   * Per-event override of the integration-wide `suppressNotifications` (silent)
   * flag. Absent ⇒ fall back to `integrations.discord.suppressNotifications`.
   */
  discordSilent?: boolean | undefined;
};

/**
 * Discord delivery transports a user can choose between. Mirrors the backend
 * `discordDeliveryModes` in `src/domain/notifications/preferences.ts`:
 *  - `bot-dm`  — private DM from the bot (works once the user links Discord)
 *  - `webhook` — post into a channel the user controls via a webhook URL
 *  - `both`    — fire whichever transport is available
 */
export const discordDeliveryModes = ["bot-dm", "webhook", "both"] as const;
export type DiscordDeliveryMode = (typeof discordDeliveryModes)[number];

/** A user-named Discord webhook: a stable opaque id, a display name, and the URL. */
export type DiscordWebhook = {
  id: string;
  name: string;
  url: string;
};

/** Maximum number of named Discord webhooks a user may configure. Mirrors backend. */
export const DISCORD_MAX_WEBHOOKS = 10;

/** Maximum length of a webhook display name. Mirrors backend. */
export const DISCORD_WEBHOOK_NAME_MAX = 60;

/** Stable id for the entry synthesized from a legacy single-webhook row. */
const DISCORD_DEFAULT_WEBHOOK_ID = "default";

/** Accepted Discord webhook URL prefixes (mirrors `discordClient.ts`). */
const DISCORD_WEBHOOK_URL_PREFIXES = [
  "https://discord.com/api/webhooks/",
  "https://discordapp.com/api/webhooks/"
];

/**
 * Validate a Discord webhook URL client-side. Mirrors the backend
 * `isValidDiscordWebhookUrl` so the manager can reject bad input before save.
 */
export const isDiscordWebhookUrl = (url: string): boolean => {
  if (!url || url.length > 2048) return false;
  return DISCORD_WEBHOOK_URL_PREFIXES.some((prefix) => url.startsWith(prefix));
};

export type NotificationIntegrations = {
  discord?: {
    /**
     * Back-compat mirror of the DEFAULT webhook's URL. Maintained by
     * {@link normalizeNotificationPreferences}. `webhooks[]` is canonical.
     */
    webhookUrl?: string | null;
    /** Canonical named-webhook registry (capped at {@link DISCORD_MAX_WEBHOOKS}). */
    webhooks?: DiscordWebhook[];
    /** Which entry in `webhooks[]` is the default. Null ⇒ first entry. */
    defaultWebhookId?: string | null;
    suppressNotifications?: boolean;
    discordUserId?: string | null;
    delivery?: DiscordDeliveryMode;
  };
  signal?: {
    recipient?: string | null;
    note?: string | null;
  };
  whatsapp?: {
    recipient?: string | null;
    providerUrl?: string | null;
  };
};

/**
 * Preset threshold options exposed in the UI. Values written to `thresholds[]`
 * MUST be one of these — anything else gets folded into `customThresholds[]`
 * by the migration in `normalizeNotificationPreferences`. Mirrored from the
 * backend `DISK_USAGE_PRESET_THRESHOLDS` constant.
 */
export const DISK_USAGE_PRESET_THRESHOLDS = [80, 90, 95] as const;

/** Maximum number of custom thresholds a user may configure */
export const DISK_USAGE_MAX_CUSTOM_THRESHOLDS = 5;

export type DiskUsageAlertPreferences = {
  enabled: boolean;
  /** Preset thresholds the user has enabled (subset of {@link DISK_USAGE_PRESET_THRESHOLDS}) */
  thresholds: number[];
  /** Custom thresholds (1-99) the user has added beyond the presets, capped at {@link DISK_USAGE_MAX_CUSTOM_THRESHOLDS} entries */
  customThresholds: number[];
};

export type UpdatesDigestPreferences = {
  digestEnabled: boolean;
  digestHour: number; // 0-23
  digestCadence: "daily" | "weekly"; // default "daily"
  digestWeekday: number; // 0=Sunday..6=Saturday, default 1 (Monday)
  timezone: string | null; // IANA timezone string, null = use dashboard timezone
};

export type NotificationPreferences = {
  version: 1;
  channels: Record<NotificationChannel, boolean>;
  events: Record<NotificationEventKey, NotificationEventSettings>;
  diskUsage?: DiskUsageAlertPreferences;
  updates?: UpdatesDigestPreferences;
  quietHours?: {
    enabled: boolean;
    start: string;
    end: string;
  };
  integrations?: NotificationIntegrations;
  updatedAt: string;
};

export type NotificationEventMeta = {
  key: NotificationEventKey;
  title: string;
  description: string;
  comingSoon?: boolean;
  channels?: NotificationChannel[];
  badge?: string;
};

export type NotificationEventGroup = {
  id: string;
  title: string;
  description?: string;
  events: NotificationEventMeta[];
};

const defaultEvents: Record<NotificationEventKey, NotificationEventSettings> = {
  "app.offline": { enabled: true },
  "app.online": { enabled: true },
  "app.updateAvailable": { enabled: false },
  "app.updated": { enabled: false },
  "qb.updateAvailable": { enabled: false },
  "dashboard.updateAvailable": { enabled: false },
  // Admin-scoped: signal-cli is system-wide infrastructure, not a per-user
  // service. Default in-app ON; email OFF — informational, not crisis-tier.
  // Mirrors the dominant admin-only pattern (6/8 admin events email-off).
  // Email is opt-in: admins who want it can enable via the prefs panel.
  "signal.updateAvailable": { enabled: true, channels: { inApp: true, email: false } },
  // Admin-scoped: QB News announcements (System > News). In-app ON so admins
  // see new announcements by default; email OFF — informational, not
  // crisis-tier. Mirrors the signal.updateAvailable default rationale.
  "news.announcement": { enabled: true, channels: { inApp: true, email: false } },
  "system.degraded": { enabled: true },
  "system.diskUsage": { enabled: false },
  "security.newLogin": { enabled: true },
  "security.sessionRevoked": { enabled: true },
  "security.passwordChanged": { enabled: true },
  "security.mfaChanged": { enabled: true },
  "job.completed": { enabled: false },
  "job.failed": { enabled: true },
  "support.ticketUpdated": { enabled: false, comingSoon: true },
  "support.chatMessage": { enabled: false, comingSoon: true },
  "digest.daily": { enabled: false },
  "digest.weekly": { enabled: false },
  "quietHours.enabled": { enabled: false },
  // Admin-scoped defaults: in-app ON so admins retain fleet-wide visibility by
  // default (they can turn these off per-user if desired). Email defaults OFF;
  // admins opt in to email by toggling the email channel for the event.
  "admin.app.offline": { enabled: true, channels: { inApp: true, email: false } },
  "admin.app.online": { enabled: true, channels: { inApp: true, email: false } },
  "admin.vpn.killSwitch": { enabled: true, channels: { inApp: true, email: true } },
  "admin.vpn.failback": { enabled: true, channels: { inApp: true, email: true } },
  "admin.vpn.recovery": { enabled: true, channels: { inApp: true, email: false } },
  // Admin view of triggered System Monitor rules (fleet-scope dispatch path).
  // In-app ON by default so admins retain visibility; email OFF — these can be
  // noisy when thresholds are tuning and email opt-in is explicit.
  "admin.monitor.alert": { enabled: true, channels: { inApp: true, email: false } },
  // Shared PHP-FPM pool health (rutorrent / nextcloud / phpmyadmin). Offline ON
  // so admins know when a shared pool drops; online OFF so a routine restart
  // doesn't generate a recovery notification per pool. See qbx-docs#184.
  "admin.phpFpm.offline": { enabled: true, channels: { inApp: true, email: false } },
  "admin.phpFpm.online": { enabled: false, channels: { inApp: true, email: false } },
  // Attack spike: security-tier event — email ON by default so admins are notified
  // of coordinated attacks even if they have the dashboard closed. In-app also ON.
  "admin.security.attackSpike": { enabled: true, channels: { inApp: true, email: true } },
  // Inter-instance transfer activity (request / send / complete / fail). In-app ON
  // so admins see transfer offers and outcomes by default; email OFF —
  // informational, mirrors the dominant admin-only default.
  "admin.transfer.activity": { enabled: true, channels: { inApp: true, email: false } }
};

const cloneEvents = (
  events: Record<NotificationEventKey, NotificationEventSettings>
): Record<NotificationEventKey, NotificationEventSettings> =>
  Object.fromEntries(
    notificationEventKeys.map((key) => {
      const value = events[key];
      return [
        key,
        {
          enabled: value.enabled,
          comingSoon: value.comingSoon,
          channels: value.channels ? { ...value.channels } : undefined,
          discordWebhookId: value.discordWebhookId,
          discordSilent: value.discordSilent
        }
      ];
    })
  ) as Record<NotificationEventKey, NotificationEventSettings>;

const mergeEvents = (
  base: Record<NotificationEventKey, NotificationEventSettings>,
  incoming?: Partial<Record<NotificationEventKey, NotificationEventSettings>>
): Record<NotificationEventKey, NotificationEventSettings> => {
  const merged = {} as Record<NotificationEventKey, NotificationEventSettings>;
  for (const key of notificationEventKeys) {
    const fallback = base[key];
    const next = incoming?.[key];
    const mergedChannels = next?.channels
      ? { ...(fallback.channels ?? {}), ...next.channels }
      : fallback.channels;

    const mergedWebhookId =
      next?.discordWebhookId !== undefined ? next.discordWebhookId : fallback.discordWebhookId;
    const mergedSilent =
      next?.discordSilent !== undefined ? next.discordSilent : fallback.discordSilent;

    merged[key] = {
      enabled: next?.enabled ?? fallback.enabled,
      ...(mergedChannels ? { channels: mergedChannels } : {}),
      ...(fallback.comingSoon ?? next?.comingSoon ? { comingSoon: fallback.comingSoon ?? next?.comingSoon } : {}),
      ...(mergedWebhookId !== undefined ? { discordWebhookId: mergedWebhookId } : {}),
      ...(mergedSilent !== undefined ? { discordSilent: mergedSilent } : {})
    };
  }
  return merged;
};

const quietHoursDefaults = {
  enabled: false,
  start: "22:00",
  end: "07:00"
} as const;

const diskUsageDefaults: DiskUsageAlertPreferences = {
  enabled: false,
  thresholds: [90],
  customThresholds: []
};

const updatesDigestDefaults: UpdatesDigestPreferences = {
  digestEnabled: false,
  digestHour: 9,
  digestCadence: "daily",
  digestWeekday: 1,
  timezone: null,
};

/**
 * Sanitize a numeric array: round to int, clamp to 1-99, dedupe, sort ascending.
 * Returns an empty array if the input has no valid entries (does NOT inject
 * defaults — callers decide fallback semantics). Mirrors the backend.
 */
const sanitizeThresholdList = (values: number[] | undefined): number[] => {
  if (!values || values.length === 0) return [];
  const unique = Array.from(
    new Set(
      values
        .map((value) => Math.round(value))
        .filter((value) => Number.isFinite(value) && value >= 1 && value <= 99)
    )
  );
  unique.sort((a, b) => a - b);
  return unique;
};

const PRESET_SET = new Set<number>(DISK_USAGE_PRESET_THRESHOLDS);

/**
 * Merge incoming disk-usage prefs with fallback. Performs the legacy migration:
 *   - `customThreshold` (singular, legacy) is folded into `customThresholds[]`
 *   - `thresholds[]` is filtered to only preset values; non-preset values
 *     leak into `customThresholds[]` so user data is never lost
 *   - `customThresholds[]` is deduped, sorted, and capped at the max entry count
 *   - presets always win over customs (a value can't be in both buckets)
 *
 * Idempotent: feeding the output back in produces the same result. Mirrors
 * the backend `mergeDiskUsage`.
 */
const mergeDiskUsage = (
  fallback: DiskUsageAlertPreferences,
  incoming?: {
    enabled?: boolean | undefined;
    thresholds?: number[] | undefined;
    customThresholds?: number[] | undefined;
    customThreshold?: number | null | undefined;
  }
): DiskUsageAlertPreferences => {
  if (!incoming) return fallback;

  const rawThresholds = sanitizeThresholdList(incoming.thresholds);
  const presetThresholds = rawThresholds.filter((v) => PRESET_SET.has(v));
  const leakedCustoms = rawThresholds.filter((v) => !PRESET_SET.has(v));

  const legacyCustom =
    typeof incoming.customThreshold === "number" && Number.isFinite(incoming.customThreshold)
      ? [Math.min(99, Math.max(1, Math.round(incoming.customThreshold)))]
      : [];

  const combinedCustoms = sanitizeThresholdList([
    ...(incoming.customThresholds ?? []),
    ...leakedCustoms,
    ...legacyCustom
  ]).filter((v) => !PRESET_SET.has(v));

  const cappedCustoms = combinedCustoms.slice(0, DISK_USAGE_MAX_CUSTOM_THRESHOLDS);

  return {
    enabled: incoming.enabled ?? fallback.enabled,
    thresholds: presetThresholds,
    customThresholds: cappedCustoms
  };
};

const mergeQuietHours = (
  fallback: NotificationPreferences["quietHours"] | undefined,
  incoming?: NotificationPreferences["quietHours"]
): NonNullable<NotificationPreferences["quietHours"]> => {
  const base = fallback ?? quietHoursDefaults;
  if (!incoming) return base;
  return {
    enabled: incoming.enabled ?? base.enabled,
    start: incoming.start ?? base.start,
    end: incoming.end ?? base.end
  };
};

/**
 * Validate an IANA timezone string by attempting to construct an
 * Intl.DateTimeFormat. Returns the string if valid, null otherwise.
 */
const validateTimezone = (tz: string | null | undefined): string | null => {
  if (!tz) return null;
  try {
    Intl.DateTimeFormat("en-US", { timeZone: tz });
    return tz;
  } catch {
    return null;
  }
};

const mergeUpdatesDigest = (
  fallback: UpdatesDigestPreferences,
  incoming?: Partial<UpdatesDigestPreferences>
): UpdatesDigestPreferences => {
  if (!incoming) return fallback;
  return {
    digestEnabled: incoming.digestEnabled ?? fallback.digestEnabled,
    digestHour:
      typeof incoming.digestHour === "number" &&
      incoming.digestHour >= 0 &&
      incoming.digestHour <= 23
        ? incoming.digestHour
        : fallback.digestHour,
    digestCadence:
      incoming.digestCadence === "daily" || incoming.digestCadence === "weekly"
        ? incoming.digestCadence
        : fallback.digestCadence,
    digestWeekday:
      typeof incoming.digestWeekday === "number" &&
      Number.isInteger(incoming.digestWeekday) &&
      incoming.digestWeekday >= 0 &&
      incoming.digestWeekday <= 6
        ? incoming.digestWeekday
        : fallback.digestWeekday,
    timezone: incoming.timezone !== undefined
      ? validateTimezone(incoming.timezone)
      : fallback.timezone,
  };
};

/** Loose shape of an inbound discord integration block (server payload or local cache). */
type DiscordIntegrationInput = {
  webhookUrl?: string | null | undefined;
  webhooks?: Array<{ id?: string; name?: string; url?: string }> | undefined;
  defaultWebhookId?: string | null | undefined;
  suppressNotifications?: boolean | undefined;
  discordUserId?: string | null | undefined;
  delivery?: DiscordDeliveryMode | undefined;
};

/**
 * Build the canonical named-webhook registry from either the new `webhooks[]`
 * array or a legacy single `webhookUrl`. Mirrors the backend resolver: entries
 * are trimmed, de-duplicated by id, name-capped, capped at
 * {@link DISCORD_MAX_WEBHOOKS}, and any with an empty url dropped so a broken
 * entry never lingers. A legacy row with only `webhookUrl` synthesizes exactly
 * one default entry. Idempotent — feeding the output back in is a no-op.
 */
const resolveDiscordWebhookRegistry = (
  discord: DiscordIntegrationInput
): { webhooks: DiscordWebhook[]; defaultWebhookId: string | null; webhookUrl: string | null } => {
  const seen = new Set<string>();
  const cleaned: DiscordWebhook[] = [];
  for (const entry of discord.webhooks ?? []) {
    if (cleaned.length >= DISCORD_MAX_WEBHOOKS) break;
    const id = typeof entry?.id === "string" ? entry.id.trim() : "";
    const rawName = typeof entry?.name === "string" ? entry.name.trim().slice(0, DISCORD_WEBHOOK_NAME_MAX) : "";
    const url = typeof entry?.url === "string" ? entry.url.trim() : "";
    if (!id || !url || seen.has(id)) continue;
    seen.add(id);
    cleaned.push({ id, name: rawName || id, url });
  }

  const legacyUrl = typeof discord.webhookUrl === "string" ? discord.webhookUrl.trim() : "";
  if (cleaned.length === 0 && legacyUrl) {
    cleaned.push({ id: DISCORD_DEFAULT_WEBHOOK_ID, name: "Default", url: legacyUrl });
  }

  const first = cleaned[0];
  if (!first) {
    return { webhooks: [], defaultWebhookId: null, webhookUrl: null };
  }

  const requested = typeof discord.defaultWebhookId === "string" ? discord.defaultWebhookId.trim() : "";
  const defaultEntry = cleaned.find((w) => w.id === requested) ?? first;
  return {
    webhooks: cleaned,
    defaultWebhookId: defaultEntry.id,
    webhookUrl: defaultEntry.url
  };
};

export const createDefaultNotificationPreferences = (): NotificationPreferences => ({
  version: 1,
  channels: {
    inApp: true,
    email: false,
    realtime: true,
    discord: false,
    signal: false,
    whatsapp: false
  },
  events: cloneEvents(defaultEvents),
  diskUsage: diskUsageDefaults,
  updates: updatesDigestDefaults,
  quietHours: quietHoursDefaults,
  integrations: {},
  updatedAt: new Date().toISOString()
});

export const normalizeNotificationPreferences = (
  input?: Partial<NotificationPreferences> | unknown
): NotificationPreferences => {
  const base = createDefaultNotificationPreferences();
  if (!input || typeof input !== "object") {
    return base;
  }

  const candidate = input as Partial<NotificationPreferences>;
  const channels = candidate.channels
    ? { ...base.channels, ...candidate.channels }
    : base.channels;

  const events = mergeEvents(base.events, candidate.events);
  // Pass through with the legacy `customThreshold` field intact so mergeDiskUsage
  // can fold it into the new `customThresholds[]` for legacy server payloads.
  const diskUsage = mergeDiskUsage(
    base.diskUsage ?? diskUsageDefaults,
    candidate.diskUsage as Parameters<typeof mergeDiskUsage>[1]
  );
  const updates = mergeUpdatesDigest(base.updates ?? updatesDigestDefaults, candidate.updates);
  const quietHours = mergeQuietHours(base.quietHours, candidate.quietHours);
  const integrations: NotificationIntegrations = {};
  if (candidate.integrations?.discord) {
    const discordInput = candidate.integrations.discord as DiscordIntegrationInput;
    const registry = resolveDiscordWebhookRegistry(discordInput);
    integrations.discord = {
      webhookUrl: registry.webhookUrl,
      webhooks: registry.webhooks,
      defaultWebhookId: registry.defaultWebhookId,
      suppressNotifications: discordInput.suppressNotifications ?? true,
      discordUserId: discordInput.discordUserId ?? null,
      delivery: discordInput.delivery ?? "bot-dm",
    };
  }
  if (candidate.integrations?.signal) {
    integrations.signal = {
      recipient: candidate.integrations.signal.recipient ?? null,
      note: candidate.integrations.signal.note ?? null
    };
  }
  if (candidate.integrations?.whatsapp) {
    integrations.whatsapp = {
      recipient: candidate.integrations.whatsapp.recipient ?? null,
      providerUrl: candidate.integrations.whatsapp.providerUrl ?? null
    };
  }

  return {
    ...base,
    channels,
    events,
    diskUsage,
    updates,
    quietHours,
    integrations,
    updatedAt: candidate.updatedAt ?? base.updatedAt
  };
};

export const areNotificationPreferencesEqual = (
  left: NotificationPreferences,
  right: NotificationPreferences
) => JSON.stringify(left) === JSON.stringify(right);

export const notificationEventGroups: NotificationEventGroup[] = [
  {
    id: "applications",
    title: "Applications & Services",
    description: "Health and lifecycle alerts for QuickBox-managed apps.",
    events: [
      {
        key: "app.offline",
        title: "Application goes offline",
        description: "An installed app stops responding or a service is down.",
        channels: ["inApp", "realtime", "email", "discord", "signal", "whatsapp"]
      },
      {
        key: "app.online",
        title: "Application comes online",
        description: "A previously offline application is back and healthy.",
        channels: ["inApp", "realtime", "discord", "signal", "whatsapp"]
      },
      {
        key: "system.degraded",
        title: "Critical service degraded",
        description: "Reverse proxy, auth, or database health issues detected.",
        channels: ["inApp", "realtime", "email", "discord", "signal", "whatsapp"]
      },
      {
        key: "system.diskUsage",
        title: "Disk usage threshold reached",
        description: "Trigger when storage usage crosses your configured thresholds.",
        channels: ["inApp", "email", "discord", "signal", "whatsapp"]
      },
      {
        key: "app.updateAvailable",
        title: "Application update available",
        description: "A managed app has a new version ready to install.",
        channels: ["inApp", "email", "discord"]
      },
      {
        key: "qb.updateAvailable",
        title: "QuickBox update available",
        description: "A new version of the QuickBox CLI or platform is available.",
        channels: ["inApp", "email", "discord"]
      },
      {
        key: "dashboard.updateAvailable",
        title: "Dashboard update available",
        description: "A new version of the v4 dashboard is ready to install.",
        channels: ["inApp", "email", "discord"]
      },
      {
        key: "signal.updateAvailable",
        title: "signal-cli update available",
        description: "A new version of the signal-cli binary is available.",
        channels: ["inApp", "email", "discord"]
      },
      {
        key: "app.updated",
        title: "Application updated successfully",
        description: "A managed app finished updating without errors.",
        channels: ["inApp", "realtime", "discord"]
      }
    ]
  },
  {
    id: "security",
    title: "Account & Security",
    description: "Keep tabs on account changes and access attempts.",
    events: [
      {
        key: "security.newLogin",
        title: "New login detected",
        description: "Alert when a new device or IP signs in to your account.",
        channels: ["inApp", "email", "discord", "signal", "whatsapp"]
      },
      {
        key: "security.sessionRevoked",
        title: "Session revoked",
        description: "When a session is signed out or removed from another device.",
        channels: ["inApp", "email", "discord", "signal", "whatsapp"]
      },
      {
        key: "security.passwordChanged",
        title: "Password changed",
        description: "Confirmations when your password is updated.",
        channels: ["inApp", "email", "discord", "signal", "whatsapp"]
      },
      {
        key: "security.mfaChanged",
        title: "2FA / Passkey changes",
        description: "When multi-factor authentication or passkeys are added or removed.",
        channels: ["inApp", "email", "discord", "signal", "whatsapp"]
      }
    ]
  },
  {
    id: "automation",
    title: "Jobs & Automation",
    description: "Background tasks and scheduled jobs.",
    events: [
      {
        key: "job.completed",
        title: "Background task completed",
        description: "Long-running jobs (e.g., library scans) finished.",
        channels: ["inApp", "realtime", "discord", "signal", "whatsapp"]
      },
      {
        key: "job.failed",
        title: "Background task failed",
        description: "High-value failures that may need attention.",
        channels: ["inApp", "realtime", "email", "discord", "signal", "whatsapp"]
      }
    ]
  },
  {
    id: "support",
    title: "Support & Collaboration",
    description: "Tickets and chat notifications (future-facing).",
    events: [
      {
        key: "support.ticketUpdated",
        title: "Support ticket updates",
        description: "New or updated tickets assigned to you.",
        comingSoon: true,
        channels: ["inApp", "email", "discord"]
      },
      {
        key: "support.chatMessage",
        title: "Direct message or mention",
        description: "Mentions and new chat messages in QuickBox rooms.",
        comingSoon: true,
        channels: ["inApp", "realtime", "discord", "signal", "whatsapp"]
      }
    ]
  },
  {
    id: "digest",
    title: "Digest & Noise Controls",
    description: "Control digests and quiet hours for calmer alerts.",
    events: [
      {
        key: "digest.daily",
        title: "Daily digest",
        description: "Roll-up of key events each day.",
        channels: ["email"]
      },
      {
        key: "digest.weekly",
        title: "Weekly digest",
        description: "Sunday summary of your server activity.",
        channels: ["email"]
      },
      {
        key: "quietHours.enabled",
        title: "Quiet hours",
        description: "Mute non-critical alerts between your preferred hours.",
        channels: ["inApp", "realtime", "email", "discord", "signal", "whatsapp"]
      }
    ]
  },
  {
    id: "admin-fleet",
    title: "Fleet Visibility",
    description: "Cross-user alerts surfaced to admin users.",
    events: [
      {
        key: "admin.app.offline",
        title: "Any user's application goes offline",
        description: "An installed app on any user's account stops responding.",
        channels: ["inApp", "email", "discord"]
      },
      {
        key: "admin.app.online",
        title: "Any user's application comes online",
        description: "A previously offline app on any user's account is back and healthy.",
        channels: ["inApp", "discord"]
      },
      {
        key: "admin.vpn.killSwitch",
        title: "VPN kill switch triggered",
        description: "A user's VPN tunnel became unreachable and their app was stopped.",
        channels: ["inApp", "email", "discord"]
      },
      {
        key: "admin.vpn.failback",
        title: "VPN routing failback",
        description: "A user's VPN tunnel became unreachable and their app reverted to the host network.",
        channels: ["inApp", "email", "discord"]
      },
      {
        key: "admin.vpn.recovery",
        title: "VPN auto-recovery result",
        description: "Outcome of an automatic VPN recovery attempt — recovered or gave up.",
        channels: ["inApp", "email", "discord"]
      },
      {
        key: "admin.monitor.alert",
        title: "System Monitor rule triggered",
        description: "A System Monitor rule crossed its threshold on a host-wide metric or service.",
        channels: ["inApp", "email", "discord"]
      },
      {
        key: "admin.phpFpm.offline",
        title: "Shared PHP pool offline",
        description: "A PHP-FPM version used by at least one installed app (rutorrent, nextcloud, phpmyadmin) is no longer running.",
        channels: ["inApp", "email", "discord"]
      },
      {
        key: "admin.phpFpm.online",
        title: "Shared PHP pool recovered",
        description: "A previously-offline PHP-FPM version is running again.",
        channels: ["inApp", "email", "discord"]
      },
      {
        key: "admin.security.attackSpike",
        title: "Attack spike detected",
        description: "Many IPs banned by fail2ban in a short window — a coordinated attack may be underway.",
        channels: ["inApp", "email", "discord"]
      },
      {
        key: "news.announcement",
        title: "QB News announcements",
        description: "A new QB News announcement is published, or an existing one is updated (System → News).",
        channels: ["inApp", "email", "discord"]
      }
    ]
  },
  {
    id: "transfers",
    title: "Transfers",
    description: "Inter-instance transfer activity surfaced to admin users.",
    events: [
      {
        key: "admin.transfer.activity",
        title: "Transfer activity",
        description: "Alerts when an inter-instance transfer request arrives, sends, completes, or fails.",
        channels: ["inApp", "email", "discord"]
      }
    ]
  }
];

export const getEffectiveEventChannel = (
  preferences: NotificationPreferences,
  eventKey: NotificationEventKey,
  channel: NotificationChannel
) => {
  const event = preferences.events[eventKey];
  return event?.channels?.[channel] ?? preferences.channels[channel] ?? false;
};

/**
 * Resolve which named webhook id an event effectively posts to. Mirrors the
 * backend {@link resolveDiscordWebhookUrl} order but returns the id so the UI can
 * show the effective target: event override → default → first entry → null.
 * A referenced-but-deleted id falls through to the default (never dropped).
 */
export const resolveEffectiveWebhookId = (
  discord: NotificationIntegrations["discord"] | undefined,
  event: NotificationEventSettings | undefined
): string | null => {
  const webhooks = discord?.webhooks ?? [];
  if (webhooks.length === 0) return null;

  const eventId = event?.discordWebhookId;
  if (eventId && webhooks.some((w) => w.id === eventId)) return eventId;

  if (discord?.defaultWebhookId && webhooks.some((w) => w.id === discord.defaultWebhookId)) {
    return discord.defaultWebhookId;
  }

  return webhooks[0]?.id ?? null;
};

/**
 * Resolve the effective "silent" flag for an event: the per-event
 * `discordSilent` override wins, else the integration-wide
 * `suppressNotifications`, else true (the historical default). Mirrors backend.
 */
export const resolveEffectiveDiscordSilent = (
  discord: NotificationIntegrations["discord"] | undefined,
  event: NotificationEventSettings | undefined
): boolean => {
  if (event?.discordSilent !== undefined) return event.discordSilent;
  return discord?.suppressNotifications ?? true;
};
